import { useState, type ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  AggregatedPackage,
  PackageListState,
} from '@kirin/api-client/contracts/agent-command-events';
import { BlockToggle } from '@/components/shared/BlockToggle';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PackageListActions } from '@/hooks/api/usePackageListActions';

/**
 * "Enforcement" cell for the Packages tab (KIR-4443, AC 8 + AC 9): the shared
 * green Allowed / red Blocked switch.
 *
 * Allowed means "not blocked": moving the switch to Allowed removes the
 * package's Block rule, so it is judged by the Malicious Packages detection
 * policy like any other package. Packages have no Allow list.
 */

const DESCRIPTIONS: Record<PackageListState, string> = {
  none: 'Not blocked. Installs are permitted and still judged by the Malicious Packages detection policy.',
  blocked: 'Blocked. Coding agents are denied the install before it runs.',
};

/**
 * A rule is name + ecosystem and nothing else, so it covers every version and
 * every package manager that installs from that registry (npm, pnpm, yarn and
 * npx all resolve to `npm`). Said in the confirmation, because an admin
 * blocking one risky release would otherwise reasonably assume they had
 * blocked only that.
 */
function ruleScopeNote(ecosystem: string): string {
  return `Applies to every version, from every package manager that installs from ${ecosystem}.`;
}

/**
 * Package names are often ordinary words or verbs ("is-odd", "request", "run"),
 * so a bare "Block is-odd" does not read as an action on a package.
 */
function packageSubject(name: string): string {
  return `"${name}" package`;
}

interface PackageStateCellProps {
  row: AggregatedPackage;
  actions: PackageListActions;
  /**
   * Whether the viewer may change the lists. Read-only viewers still see the
   * state — it is information, not an action.
   */
  canEdit: boolean;
}

export function PackageStateCell({
  row,
  actions,
  canEdit,
}: PackageStateCellProps): ReactElement {
  const state = row.listState;
  const blocked = state === 'blocked';
  const [confirmingBlock, setConfirmingBlock] = useState(false);

  // A rule is name + ecosystem. An unmapped manager has no ecosystem, so no
  // rule can name the package and there is nothing to offer.
  const ecosystem = row.ecosystem;
  const name = row.packageName?.trim() ?? '';
  const actionable = canEdit && ecosystem != null && name !== '';

  if (!actionable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'text-xs font-medium',
              blocked ? 'text-red-600' : 'text-green-600'
            )}
            tabIndex={0}
          >
            {blocked ? 'Blocked' : 'Allowed'}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {DESCRIPTIONS[state]}
        </TooltipContent>
      </Tooltip>
    );
  }

  const pkg = { name, ecosystem };
  const pending = actions.isPending(pkg);

  return (
    // The row is click-to-drill-down; without this a click in the
    // confirmation (portaled, but React events still bubble here) would also
    // open the occurrences dialog.
    <div
      className="flex items-center gap-2"
      onClick={event => event.stopPropagation()}
    >
      {/*
        Block is the one move that can stop other people's installs, so it
        alone asks first. Allowed only returns the package to detection.
      */}
      <BlockToggle
        blocked={blocked}
        name={packageSubject(name)}
        onAllow={() => void actions.clear(pkg)}
        onRequestBlock={() => setConfirmingBlock(true)}
        disabled={pending}
        switchClassName="data-[state=checked]:bg-green-600"
      />
      {pending && (
        <Loader2
          className="size-3 shrink-0 animate-spin text-muted-foreground"
          aria-label="Updating package lists"
        />
      )}
      <ConfirmationDialog
        isOpen={confirmingBlock}
        onClose={() => setConfirmingBlock(false)}
        onConfirm={() => {
          setConfirmingBlock(false);
          void actions.block(pkg);
        }}
        title={`Block ${packageSubject(name)}?`}
        description={
          `${
            actions.scope === 'organization'
              ? 'This applies to everyone in this organization. '
              : ''
          }${ruleScopeNote(ecosystem)} Coding agents are denied the ` +
          'install before it runs.'
        }
        confirmLabel="Block"
      />
    </div>
  );
}
